import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ShieldCheck, Sun, Heart, Users, MapPin, Plus, Sparkles, Share2 } from "lucide-react";
import { toast } from "sonner";

const ICONS = { "shield-check": ShieldCheck, sun: Sun, heart: Heart, users: Users, "map-pin": MapPin };
const LABELS = {
  vouch_received: "Vouch",
  gratitude: "Gratitude",
  kind_act: "Kind act",
  community_post: "Community",
  place_moment: "Crossed path",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

export default function KindLedger() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");
  const [heartBusy, setHeartBusy] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/kind-ledger/mine");
      setData(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const addAct = async () => {
    const t = text.trim();
    if (t.length < 3) return;
    setBusy(true);
    try {
      await api.post("/kind-acts", { text: t, kind: "kindness" });
      setText("");
      setShowForm(false);
      toast.success("Added to your ledger.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleHeart = async (actId) => {
    if (heartBusy[actId]) return;
    setHeartBusy((h) => ({ ...h, [actId]: true }));
    try {
      const { data: res } = await api.post(`/kind-acts/${actId}/heart`);
      setData((d) => ({
        ...d,
        events: (d?.events || []).map((e) =>
          e.id === actId ? { ...e, hearts_count: res.hearts, viewer_hearted: res.hearted } : e
        ),
      }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHeartBusy((h) => ({ ...h, [actId]: false }));
    }
  };

  if (!data)
    return (
      <div className="text-ink/50 min-h-[40vh] flex items-center justify-center" data-testid="ledger-loading">
        Loading your ledger…
      </div>
    );

  const stats = data.stats || {};
  const events = filter === "all" ? data.events : (data.events || []).filter((e) => e.kind === filter);

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="kind-ledger-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-ochre-300 font-mono">Living portrait</div>
          <h1 className="font-display text-5xl text-ink leading-tight mt-2" style={{ fontFamily: "Fraunces", fontWeight: 400 }}>
            The Kind <em className="italic text-ivy">Ledger.</em>
          </h1>
          <p className="text-ink/70 mt-3 max-w-2xl">
            A living record of the good you do and the good said of you. It grows slowly — like real character does.
          </p>
        </div>
        {user?.license_no && (
          <Link
            to={`/ledger/${user.license_no}`}
            data-testid="ledger-share-link"
            target="_blank"
            className="btn-pill bg-ink text-parchment hover:bg-ivy flex items-center gap-2 text-sm shrink-0"
          >
            <Share2 size={14} /> Share my Ledger
          </Link>
        )}
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "vouches", label: "Vouches", icon: ShieldCheck },
          { key: "gratitude", label: "Gratitude", icon: Sun },
          { key: "kind_acts", label: "Kind acts", icon: Heart },
          { key: "community", label: "Community", icon: Users },
          { key: "places", label: "Places", icon: MapPin },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.key} className="card-paper p-4 text-center" data-testid={`ledger-stat-${s.key}`}>
              <Icon size={16} className="mx-auto text-ivy" />
              <div className="text-2xl font-display text-ink mt-1" style={{ fontFamily: "Fraunces" }}>
                {stats[s.key] || 0}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-ink/50 mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Add a kind act */}
      <div className="card-paper p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="eyebrow">Add to your ledger</div>
            <h3 className="font-display text-xl text-ink mt-1" style={{ fontFamily: "Fraunces" }}>
              Log a small kindness.
            </h3>
          </div>
          {!showForm && (
            <button
              data-testid="ledger-add-btn"
              onClick={() => setShowForm(true)}
              className="btn-pill bg-ivy text-parchment hover:bg-ivy-600 flex items-center gap-2"
            >
              <Plus size={14} /> New entry
            </button>
          )}
        </div>
        {showForm && (
          <div className="mt-4 space-y-3">
            <textarea
              data-testid="ledger-act-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Held the lift for a stranger with a pram…"
              className="w-full px-4 py-3 rounded-2xl bg-parchment-50 border border-ink/10 text-ink"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="btn-pill bg-white border border-ink/10 text-ink">
                Cancel
              </button>
              <button
                data-testid="ledger-act-save"
                onClick={addAct}
                disabled={busy || text.trim().length < 3}
                className="btn-pill bg-ink text-parchment hover:bg-ivy disabled:opacity-50 flex items-center gap-2"
              >
                <Sparkles size={14} /> Save
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2">
        {["all", "vouch_received", "gratitude", "kind_act", "community_post", "place_moment"].map((k) => (
          <button
            key={k}
            data-testid={`ledger-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-500 ${
              filter === k ? "bg-ink text-parchment" : "bg-white border border-ink/10 text-ink hover:bg-parchment-50"
            }`}
          >
            {k === "all" ? "All" : LABELS[k]}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div className="relative pl-8">
        <div className="absolute left-3 top-2 bottom-2 w-px bg-ochre-100" />
        <div className="space-y-6">
          {events.length === 0 && (
            <div className="text-ink/50 italic" data-testid="ledger-empty">
              Nothing here yet. Kindness fills the pages, one small act at a time.
            </div>
          )}
          {events.map((e) => {
            const Icon = ICONS[e.icon] || Heart;
            return (
              <div key={e.id} className="relative" data-testid={`ledger-event-${e.id}`}>
                <div className="absolute -left-8 top-1 w-6 h-6 rounded-full bg-parchment border-2 border-ivy flex items-center justify-center">
                  <Icon size={12} className="text-ivy" />
                </div>
                <div className="card-paper p-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <div className="text-xs font-mono uppercase tracking-widest text-ochre-300">{LABELS[e.kind]}</div>
                    <div className="text-xs text-ink/50 font-mono">{fmtDate(e.at)}</div>
                  </div>
                  <div className="mt-2 font-display text-lg text-ink" style={{ fontFamily: "Fraunces" }}>
                    {e.title}
                  </div>
                  {e.body && (
                    <blockquote className="mt-2 text-ink/75 italic leading-relaxed" style={{ fontFamily: "Fraunces" }}>
                      "{e.body}"
                    </blockquote>
                  )}
                  {e.meta && (
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-ink/50 font-mono">· {e.meta}</div>
                  )}
                  {e.kind === "kind_act" && (
                    <button
                      data-testid={`heart-act-${e.id}`}
                      onClick={() => toggleHeart(e.id)}
                      disabled={heartBusy[e.id]}
                      className={`mt-3 inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-all duration-500 ${
                        e.viewer_hearted
                          ? "bg-rose-500/10 text-rose-500"
                          : "bg-white border border-ink/10 text-ink/60 hover:bg-parchment-50"
                      }`}
                    >
                      <Heart size={12} fill={e.viewer_hearted ? "currentColor" : "none"} />
                      <span>{e.hearts_count || 0}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {err && (
        <div className="text-sm text-ochre-300" data-testid="ledger-error">
          {err}
        </div>
      )}
    </div>
  );
}
